import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import LabeledBet from 'modules/betslip/betslip-view/labeled-bet/index.js';
import BetBonusPromotions from 'modules/bet-bonus-promotions/index.js';
import { isEmpty } from 'mojito/utils';
import OddsBoostBanner from 'modules/betslip/betslip-view/odds-boost-banner/index.js';

const BetslipUtils = MojitoServices.Betslip.utils;
const BetslipTypes = MojitoServices.Betslip.types;

const { FlexPane } = MojitoPresentation.Components;
const { BET_WAY, BET_TYPE, LEG_SORT } = MojitoServices.Bets.types;

export default function SystemBetsList({
    includeGroupedSingles,
    enableGroupedSinglesStaking,
    hasPendingBonus,
    currencyCode,
    betslip,
    stakeGroup,
    mojitoTools: { config },
}) {
    const bets = getBets(betslip, stakeGroup, includeGroupedSingles);
    const betElements = bets.map(bet => {
        const betStake = BetslipUtils.getBetStake(betslip, bet.id, stakeGroup);
        const eachWayAvailable = BetslipUtils.isEachWayAvailableForBet(bet);
        const eachWayActivated = BetslipUtils.isBetEachWay(betslip, bet.id, stakeGroup);
        const hasErrorInStakeGroup = BetslipUtils.hasBetSpecificError(betslip, bet.id, stakeGroup);
        const bankerBetCount = BetslipUtils.getBankerBetsCount(betslip, stakeGroup);
        const singleBetCount = BetslipUtils.getSingleBetsCount(
            betslip,
            legSort => legSort !== LEG_SORT.BET_BUILDER
        );

        // Suspend if bet is grouped singles AND grouped singles staking is disabled
        const isGroupedSinglesBet = BetslipUtils.isGroupedSingleBet(bet);
        const suspended = isGroupedSinglesBet && !enableGroupedSinglesStaking;

        return (
            <FlexPane
                key={`system-bet-${bet.id}-${stakeGroup}`}
                config={config.stakeGroupContainer}
                class="ta-SystemBetItem"
            >
                <LabeledBet
                    config={config.labeledBet}
                    bet={bet}
                    suspended={suspended}
                    betStake={betStake}
                    showStake={!suspended}
                    isEachWayAvailable={eachWayAvailable}
                    isEachWayActivated={eachWayActivated}
                    currencyCode={currencyCode}
                    hasErrors={hasErrorInStakeGroup}
                    bankerBetCount={bankerBetCount}
                    singleBetCount={singleBetCount}
                />
                <BetBonusPromotions
                    config={config.betBonusPromotions}
                    isActive={!hasPendingBonus}
                    currencyCode={currencyCode}
                    bonuses={bet.bonuses}
                />
                {bet.oddsBoostToken && (
                    <FlexPane
                        class={'ta-OddsBoostBannerContainer'}
                        config={config.oddsBoostWrapper}
                    >
                        <OddsBoostBanner
                            config={config.oddsBoostBanner}
                            oddsBoostToken={bet.oddsBoostToken}
                            betId={bet.id}
                            betWay={betStake.betWay || BET_WAY.WIN}
                            legSort={bet.legSort}
                            currencyCode={currencyCode}
                        />
                    </FlexPane>
                )}
            </FlexPane>
        );
    });
    return (
        !isEmpty(betElements) && (
            <FlexPane class={'ta-SystemBetsList'} config={config.container}>
                {betElements}
            </FlexPane>
        )
    );
}

const getBets = (betslip, stakeGroup, includeGroupedSingles) => {
    const { SINGLES, MULTIPLES } = BetslipTypes.STAKE_GROUP_NAME;
    let bets;
    switch (stakeGroup) {
        case SINGLES:
            bets =
                includeGroupedSingles && BetslipUtils.getGroupedSinglesBet(betslip)
                    ? [BetslipUtils.getGroupedSinglesBet(betslip)]
                    : [];
            break;
        case MULTIPLES:
            bets = BetslipUtils.getMultiBet(betslip) ? [BetslipUtils.getMultiBet(betslip)] : [];
            break;
        default:
            bets = BetslipUtils.getSystemBets(betslip, includeGroupedSingles);
            break;
    }

    // 1. Sort bets according to rank
    // 2. Put single line bet at the top
    return bets
        .filter(bet => bet.betType !== BET_TYPE.UNKNOWN)
        .sort(BetslipUtils.betRankComparator)
        .sort(BetslipUtils.oneLineComparator);
};
